import { Tooltip } from 'antd';
import Image from 'next/image';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ProfileChecked from '../../../assets/icons/ProfileChecked';
import emptyProfilePhoto from '../../../assets/images/emptyProfilePhoto.png';
import theme from '../../../assets/theme';
import { LessonMediaPreFetcher } from '../../../hooks/useMediaPreFetcher';
import { useTemplateCreatorPhoto } from '../../../hooks/useTemplateCreatorPhoto';
import {
  DiscoveredTemplate,
  IndexedTemplate,
} from '../../../services/backendService/types';
import { zIndexes } from '../../../utils/zIndexes';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { ProfileCircle } from '../../ProfileCircle';
import { Slide } from '../../SlideComponents/slide.types';
import SlideWrapperViwer from '../../SlideComponents/SlideWrapperViewer';

const DesignerLessonCard = ({
  template,
  onClick,
  isHovering,
  setSearchValue,
  style,
}: {
  template: DiscoveredTemplate | IndexedTemplate;
  onClick?: (templateId: string) => void;
  setSearchValue: Dispatch<SetStateAction<string | undefined>>;
  isHovering: boolean;
  style?: React.CSSProperties;
}) => {
  const { t } = useTranslation('Host');
  const { thumbnail: creatorThumbnail } = useTemplateCreatorPhoto({
    creatorThumbnailId: template.creatorThumbnailId,
    isPrivate: false,
  });

  const slides = useMemo(() => {
    return (template.slides || []).filter(
      r => r.type !== 'voting' && r.type !== 'podium' && r.type !== 'responsegrid',
    );
  }, [template.slides]);
  const slidesCount = slides.length;
  const [selectedSlideIndex, setSelectedSlideIndex] = useState<number>(0);

  useEffect(() => {
    if (isHovering) {
      if (selectedSlideIndex === 0 && slides.length > 1) {
        setSelectedSlideIndex(selectedSlideIndex + 1);
      } else {
        const timeout = setTimeout(() => {
          if (selectedSlideIndex === slides.length - 1) {
            setSelectedSlideIndex(0);
          } else {
            setSelectedSlideIndex(selectedSlideIndex + 1);
          }
        }, 1500);
        return () => clearTimeout(timeout);
      }
    } else {
      setSelectedSlideIndex(0);
    }
  }, [isHovering, slides.length, selectedSlideIndex]);
  const selectedSlide = useMemo(() => {
    return slides[selectedSlideIndex];
  }, [slides, selectedSlideIndex]);

  if (isHovering && slides.length > 1)
    return (
      <HoveringLessonCardContainer
        creatorThumbnail={creatorThumbnail}
        onClick={() => onClick && onClick(template.id)}
        style={style ? style : {}}
        setSearchValue={setSearchValue}
        slidesCount={slidesCount}
        template={template}
        slides={slides}
      />
    );

  return (
    <LessonCardContainer
      onClick={() => onClick && onClick(template.id)}
      style={style ? style : {}}
    >
      <ImageWrapper style={{ position: 'relative' }}>
        <NumberOfSlides>
          <>{slidesCount} slides</>
        </NumberOfSlides>

        {selectedSlide && (
          <SlideWrapperViwer
            aspect={{ height: 1080, width: 1920 }}
            slide={selectedSlide}
            backgroundMedia={selectedSlide.backgroundMedia}
            resultView={false}
            showStaticPoll={true}
            hideLogo={false}
            slides={slides}
            isDiscoverTemplate={true}
          ></SlideWrapperViwer>
        )}
      </ImageWrapper>

      <ProfileWrapper>
        <ProfileSection>
          <ProfileCircle $radius={40}>
            <Image
              style={{ borderRadius: '50%' }}
              width={'100%'}
              height={'100%'}
              src={creatorThumbnail || emptyProfilePhoto}
              alt="profile"
            />
          </ProfileCircle>
          <ProfileInfoContainer>
            <h4>{template.title}</h4>

            <AuthorSection>
              <Tooltip title={t('Verified')}>
                <IconWrapper height="16px" width="16px" style={{ flex: 0 }}>
                  <ProfileChecked />
                </IconWrapper>
              </Tooltip>
              <div style={{ flex: '1 1 auto' }}>
                <p>{template.creatorName}</p>
              </div>
            </AuthorSection>
          </ProfileInfoContainer>
        </ProfileSection>
      </ProfileWrapper>
    </LessonCardContainer>
  );
};

const HoveringLessonCardContainer = ({
  template,
  onClick,
  style,
  slidesCount,
  slides,
  creatorThumbnail,
  setSearchValue,
}: {
  template: DiscoveredTemplate | IndexedTemplate;
  onClick?: (templateId: string) => void;
  style?: React.CSSProperties;
  setSearchValue: Dispatch<SetStateAction<string | undefined>>;
  slides: Slide[];
  slidesCount: number;
  creatorThumbnail?: string;
}) => {
  const { t } = useTranslation('Host');
  const [selectedRoundIndex, setSelectedRoundIndex] = useState<number>(
    slides.length > 1 ? 1 : 0,
  );
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (selectedRoundIndex === slides.length - 1) {
        setSelectedRoundIndex(0);
      } else {
        setSelectedRoundIndex(selectedRoundIndex + 1);
      }
    }, 1500);
    return () => clearTimeout(timeout);
  }, [slides.length, selectedRoundIndex]);
  const currentSlide = slides[selectedRoundIndex];
  return (
    <LessonCardContainer style={style ? style : {}}>
      <LessonMediaPreFetcher
        slides={slides}
        isAuthenticated={true}
        useThumbnail={true}
        isDiscoveredTemplate={true}
        isOwner={false}
      />
      <ImageWrapper style={{ position: 'relative' }}>
        <NumberOfSlides>
          <>{slidesCount} slides</>
        </NumberOfSlides>
        <span onClick={() => onClick && onClick(template.id)}>
          {currentSlide && (
            <SlideWrapperViwer
              aspect={{ height: 1080, width: 1920 }}
              slide={currentSlide}
              backgroundMedia={currentSlide.backgroundMedia}
              resultView={false}
              showStaticPoll={true}
              hideLogo={false}
              slides={slides}
              isDiscoverTemplate={true}
            ></SlideWrapperViwer>
          )}
        </span>
      </ImageWrapper>

      <ProfileWrapper>
        <ProfileSection>
          <ProfileCircle
            $radius={40}
            onClick={() => setSearchValue(template.creatorName)}
          >
            <Image
              style={{ borderRadius: '50%' }}
              width={'100%'}
              height={'100%'}
              src={creatorThumbnail || emptyProfilePhoto}
              alt="profile"
            />
          </ProfileCircle>
          <ProfileInfoContainer>
            <h4 onClick={() => onClick && onClick(template.id)}>{template.title}</h4>

            <AuthorSection>
              <Tooltip title={t('Verified')}>
                <IconWrapper height="16px" width="16px" style={{ flex: 0 }}>
                  <ProfileChecked />
                </IconWrapper>
              </Tooltip>
              <div
                style={{ flex: '1 1 auto' }}
                onClick={() => setSearchValue(template.creatorName)}
              >
                <p>{template.creatorName}</p>
              </div>
            </AuthorSection>
          </ProfileInfoContainer>
        </ProfileSection>
      </ProfileWrapper>
    </LessonCardContainer>
  );
};
const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  transition: 0.5s;
  overflow-x: hidden;
  width: 100%;

  p {
    margin-bottom: 0;
    margin-left: 5px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    &:hover {
      text-decoration: underline;
    }
  }
  svg {
    color: #ffa55b;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 175px;
  width: 100%;
  overflow: hidden;

  img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }

  @media (max-width: 400px) {
    height: 60%;
  }
`;

const NumberOfSlides = styled.span`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: ${theme.colors.translucentBlack};
  border-radius: 5px;
  color: ${theme.colors.white};
  padding: 2px 5px;
  font-size: 14px;
  z-index: ${zIndexes('CREATOR_SLIDE_VIEW')};
`;
const ProfileWrapper = styled.div`
  height: 60px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 10px;
  a {
    &:hover {
      text-decoration: underline;
    }
  }
  @media (max-width: 400px) {
    height: 40%;
  }
`;

const ProfileSection = styled.div`
  width: 100%;
  padding-top: 10px;
  display: flex;
  align-items: flex-start;

  justify-content: space-between;

  h2 {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: calc(100% - 0px);
  }

  h3 {
    margin-bottom: 0px;
    display: inline-flex;
    align-items: center;
    color: ${theme.colors.text.dark};
  }

  span {
    display: flex;
    margin: 0px 5px 4px 0px;
  }
`;

const ProfileInfoContainer = styled.div`
  padding-left: 10px;
  flex: 1;
  min-width: 0;

  h4 {
    font-weight: bold;
    font-size: 14px;
    width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    font-size: 16px;
    margin-bottom: 0px;
  }

  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

export const LessonCardContainer = styled.div`
  cursor: pointer;
  background: ${theme.colors.white};
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.1);
  margin: 0px auto;

  h2 {
    font-weight: bold;
  }

  @media (max-width: 400px) {
    border-radius: 5px;
  }
  @media (max-width: 900px) {
    max-width: 310px;
  }
`;

export default DesignerLessonCard;
